import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api, { getErrorMessage } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { SourceBadge } from '../components/StatusBadge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  ClipboardList, User, Building2, Wrench, ArrowRight, Camera,
  ChevronRight, MoreHorizontal, CheckCircle, XCircle, Play, Eye,
  Archive, Trash2, List
} from 'lucide-react';
import { toast } from 'sonner';

const COLUMNS = [
  { key: 'todo', color: 'border-blue-500', bg: 'bg-blue-500/10', icon: ClipboardList },
  { key: 'doing', color: 'border-amber-500', bg: 'bg-amber-500/10', icon: Play },
  { key: 'review', color: 'border-purple-500', bg: 'bg-purple-500/10', icon: Eye },
  { key: 'done', color: 'border-green-500', bg: 'bg-green-500/10', icon: CheckCircle },
];

const PRIORITY_COLORS = {
  CRITICAL: 'bg-red-600',
  HIGH: 'bg-red-500',
  MEDIUM: 'bg-amber-500',
  LOW: 'bg-green-500',
};

export default function KanbanBoard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [board, setBoard] = useState({ todo: [], doing: [], review: [], done: [] });
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [fBranch, setFBranch] = useState('all');
  const [fPriority, setFPriority] = useState('all');
  const [showArchived, setShowArchived] = useState(false); // Default FALSE for Kanban

  // Modal states
  const [rejectModal, setRejectModal] = useState(null); // { wo, reason }
  const [rejectReason, setRejectReason] = useState('');
  const [proofModal, setProofModal] = useState(null); // wo needing proof
  const [moveModal, setMoveModal] = useState(null); // { wo, options } for mobile
  const [processing, setProcessing] = useState('');
  const [loadError, setLoadError] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null); // { wo, type: 'delete'|'archive' }

  const isManager = ['admin', 'supervisor', 'super_admin', 'branch_manager'].includes(user?.role);
  const isTech = user?.role === 'technician';
  const isRequester = user?.role === 'requester';

  const loadBoard = useCallback(async () => {
    try {
      const params = { include_archived: showArchived };
      if (fBranch !== 'all') params.branch_id = fBranch;
      if (fPriority !== 'all') params.priority = fPriority;
      const { data } = await api.get('/work-orders/kanban', { params });
      setBoard(data.columns);
      setCounts(data.counts);
      setLoadError(null);
    } catch (err) {
      const status = err.response?.status;
      const serverMsg = err.response?.data?.detail || err.response?.data?.message;
      const code = err.response?.data?.code;
      const isSuperAdmin = user?.role === 'super_admin' || user?.email === 'ahmad.disi@unknownroom.com';

      if (isSuperAdmin && serverMsg) {
        const debugMsg = `[${status || 'NET'}] ${code || ''} ${serverMsg}`;
        toast.error(debugMsg);
        setLoadError(debugMsg);
      } else {
        toast.error(t('toast.error'));
        setLoadError(null);
      }
    }
    finally { setLoading(false); }
  }, [fBranch, fPriority, showArchived, t, user]);

  useEffect(() => {
    api.get('/branches').then(r => setBranches(r.data)).catch(() => {});
    loadBoard();
  }, [loadBoard]);

  const moveCard = async (wo, toStatus, reason) => {
    setProcessing(wo.id);
    try {
      const payload = { status: toStatus };
      if (reason) payload.resolution_text = reason;
      // For closing, add resolution
      if (toStatus === 'CLOSED') payload.resolution_text = reason || 'Approved';
      await api.post(`/work-orders/${wo.id}/status`, payload);
      toast.success(t('kanban.moved'));
      loadBoard();
    } catch (err) {
      const msg = getErrorMessage(err, t('toast.error'), user);
      // Check if it's a proof requirement error
      if (msg.includes('proof') || msg.includes('attachment')) {
        setProofModal(wo);
      } else {
        toast.error(msg);
      }
    } finally { setProcessing(''); }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) { toast.error(t('kanban.rejectReasonRequired')); return; }
    await moveCard(rejectModal, 'IN_PROGRESS', rejectReason);
    setRejectModal(null);
    setRejectReason('');
  };

  const getCardMoveOptions = (wo) => {
    const opts = [];
    const s = wo.status;
    if (isTech) {
      if (['OPEN', 'ASSIGNED'].includes(s)) opts.push({ label: t('kanban.startWork'), target: 'IN_PROGRESS' });
      if (['IN_PROGRESS', 'WAITING_PARTS', 'WAITING_VENDOR'].includes(s)) opts.push({ label: t('kanban.submitReview'), target: 'COMPLETED' });
    }
    if (isManager) {
      if (['OPEN', 'ASSIGNED'].includes(s)) opts.push({ label: t('kanban.startWork'), target: 'IN_PROGRESS' });
      if (s === 'COMPLETED') {
        opts.push({ label: t('kanban.approve'), target: 'CLOSED' });
        opts.push({ label: t('kanban.reject'), target: 'REJECT' });
      }
    }
    return opts;
  };

  const handleMoveAction = (wo, target) => {
    if (target === 'REJECT') {
      setRejectModal(wo);
    } else {
      moveCard(wo, target);
    }
    setMoveModal(null);
  };

  const handleConfirmAction = async () => {
    if (!confirmAction) return;
    const { wo, type } = confirmAction;
    setProcessing(wo.id);
    try {
      if (type === 'archive') {
        await api.post(`/work-orders/${wo.id}/archive`);
        toast.success('Work order archived');
      } else {
        await api.delete(`/work-orders/${wo.id}`);
        toast.success('Work order deleted');
      }
      loadBoard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setProcessing('');
      setConfirmAction(null);
    }
  };

  // Desktop drag handlers
  const handleDragStart = (e, wo) => {
    if (isRequester) { e.preventDefault(); return; }
    e.dataTransfer.setData('wo', JSON.stringify(wo));
  };

  const handleDrop = (e, colKey) => {
    e.preventDefault();
    const wo = JSON.parse(e.dataTransfer.getData('wo'));
    const targetStatuses = { todo: 'ASSIGNED', doing: 'IN_PROGRESS', review: 'COMPLETED', done: 'CLOSED' };
    const target = targetStatuses[colKey];
    if (!target || wo.status === target) return;
    if (target === 'CLOSED' && wo.status === 'COMPLETED' && isManager) {
      moveCard(wo, 'CLOSED', 'Approved');
    } else if (colKey === 'doing' && wo.status === 'COMPLETED' && isManager) {
      setRejectModal(wo);
    } else {
      moveCard(wo, target);
    }
  };

  const handleDragOver = (e) => e.preventDefault();

  if (loading) return <div className="flex items-center justify-center h-64 text-muted-foreground">{t('common.loading')}</div>;

  return (
    <div className="space-y-4" data-testid="kanban-board">
      {/* Header + Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="font-barlow font-bold text-2xl text-foreground tracking-tight">{t('kanban.title')}</h1>
            <p className="text-muted-foreground text-sm mt-0.5">{Object.values(counts).reduce((a, b) => a + b, 0)} {t('kanban.totalWOs')}</p>
          </div>
          {/* List View button - visible only for managers */}
          {isManager && (
            <Button
              variant="outline"
              size="sm"
              className="hidden sm:flex h-8"
              onClick={() => navigate('/work-orders')}
              data-testid="list-view-btn"
            >
              <List className="w-4 h-4 me-1.5" />
              {t('nav.listView')}
            </Button>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {!isTech && (
            <Select value={fBranch} onValueChange={setFBranch}>
              <SelectTrigger className="w-full sm:w-[180px] h-9" data-testid="kanban-branch-filter"><SelectValue placeholder={t('common.all')} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common.all')} {t('common.branches')}</SelectItem>
                {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Select value={fPriority} onValueChange={setFPriority}>
            <SelectTrigger className="w-full sm:w-[140px] h-9" data-testid="kanban-priority-filter"><SelectValue placeholder={t('workRequest.priority')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('common.all')}</SelectItem>
              <SelectItem value="CRITICAL">{t('workRequest.critical')}</SelectItem>
              <SelectItem value="HIGH">{t('workRequest.high')}</SelectItem>
              <SelectItem value="MEDIUM">{t('workRequest.medium')}</SelectItem>
              <SelectItem value="LOW">{t('workRequest.low')}</SelectItem>
            </SelectContent>
          </Select>
          {/* Show archived toggle */}
          <div className="flex items-center gap-2 ms-2">
            <Checkbox 
              id="kanban-show-archived" 
              checked={showArchived} 
              onCheckedChange={setShowArchived}
              data-testid="kanban-show-archived-checkbox"
            />
            <Label htmlFor="kanban-show-archived" className="text-sm text-muted-foreground cursor-pointer whitespace-nowrap">
              {t('workOrder.showArchived', 'Show archived')}
            </Label>
          </div>
        </div>
      </div>

      {/* Error banner (Super Admin sees details) */}
      {loadError && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-sm" data-testid="kanban-error-banner">
          <div className="flex items-center gap-2 text-red-400 font-medium">
            <XCircle className="w-4 h-4 flex-shrink-0" />
            <span>Kanban Load Failed</span>
          </div>
          <pre className="mt-1 text-xs text-red-300/80 whitespace-pre-wrap font-mono">{loadError}</pre>
          <Button size="sm" variant="outline" className="mt-2 h-7 text-xs" onClick={() => { setLoading(true); loadBoard(); }}>
            Retry
          </Button>
        </div>
      )}

      {/* Kanban Columns */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {COLUMNS.map(col => (
          <div
            key={col.key}
            className={`rounded-xl border-t-4 ${col.color} bg-muted/30 min-h-[200px]`}
            onDrop={(e) => handleDrop(e, col.key)}
            onDragOver={handleDragOver}
            data-testid={`kanban-col-${col.key}`}
          >
            <div className="flex items-center gap-2 p-3 pb-2">
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${col.bg}`}>
                <col.icon className="w-4 h-4" />
              </div>
              <span className="text-sm font-semibold text-foreground">{t(`kanban.${col.key}`)}</span>
              <Badge variant="secondary" className="ms-auto text-xs">{counts[col.key] || 0}</Badge>
            </div>
            <div className="p-2 space-y-2 max-h-[60vh] overflow-y-auto">
              {(board[col.key] || []).map(wo => (
                <WOCard
                  key={wo.id}
                  wo={wo}
                  isRequester={isRequester}
                  isManager={isManager}
                  processing={processing}
                  moveOptions={getCardMoveOptions(wo)}
                  onMove={(target) => handleMoveAction(wo, target)}
                  onMobileMenu={() => setMoveModal({ wo, options: getCardMoveOptions(wo) })}
                  onDragStart={(e) => handleDragStart(e, wo)}
                  onNavigate={() => navigate(`/work-orders/${wo.id}`)}
                  onArchive={() => setConfirmAction({ wo, type: 'archive' })}
                  onDelete={() => setConfirmAction({ wo, type: 'delete' })}
                />
              ))}
              {(board[col.key] || []).length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-6">{t('kanban.empty')}</p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Reject Modal */}
      <Dialog open={!!rejectModal} onOpenChange={() => { setRejectModal(null); setRejectReason(''); }}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader><DialogTitle className="font-barlow">{t('kanban.rejectTitle')}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <Label>{t('kanban.rejectReason')} *</Label>
            <Textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder={t('kanban.rejectPlaceholder')} rows={3} data-testid="reject-reason-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRejectModal(null); setRejectReason(''); }}>{t('common.cancel')}</Button>
            <Button onClick={handleReject} disabled={!rejectReason.trim()} className="bg-red-500 hover:bg-red-600 text-white" data-testid="confirm-reject-btn">{t('kanban.confirmReject')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Proof Required Modal */}
      <Dialog open={!!proofModal} onOpenChange={() => setProofModal(null)}>
        <DialogContent className="bg-card border-border max-w-sm">
          <DialogHeader><DialogTitle className="font-barlow">{t('kanban.proofRequired')}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground py-2">{t('kanban.proofRequiredDesc')}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProofModal(null)}>{t('common.cancel')}</Button>
            <Button onClick={() => { navigate(`/work-orders/${proofModal.id}`); setProofModal(null); }} data-testid="go-upload-proof-btn">
              <Camera className="w-4 h-4 me-2" /> {t('kanban.uploadProof')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mobile Move Modal */}
      <Dialog open={!!moveModal} onOpenChange={() => setMoveModal(null)}>
        <DialogContent className="bg-card border-border max-w-xs">
          <DialogHeader><DialogTitle className="font-barlow text-base">{t('kanban.moveTo')}</DialogTitle></DialogHeader>
          <div className="space-y-2 py-2">
            {moveModal?.options.map((opt, i) => (
              <Button key={i} variant="outline" className="w-full justify-start" onClick={() => handleMoveAction(moveModal.wo, opt.target)} data-testid={`move-option-${opt.target}`}>
                <ArrowRight className="w-4 h-4 me-2" /> {opt.label}
              </Button>
            ))}
            {(!moveModal?.options || moveModal.options.length === 0) && (
              <p className="text-sm text-muted-foreground text-center">{t('kanban.noMoves')}</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete/Archive Confirmation */}
      <Dialog open={!!confirmAction} onOpenChange={() => setConfirmAction(null)}>
        <DialogContent className="bg-card border-border max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-barlow">
              {confirmAction?.type === 'delete' ? 'Delete Work Order' : 'Archive Work Order'}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground py-2">
            {confirmAction?.type === 'delete'
              ? 'This will permanently delete this work order and all its data. This cannot be undone.'
              : 'This will hide the work order from the Kanban board. It can be restored later.'}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmAction(null)}>Cancel</Button>
            <Button
              variant={confirmAction?.type === 'delete' ? 'destructive' : 'default'}
              onClick={handleConfirmAction}
              disabled={processing === confirmAction?.wo?.id}
              data-testid={`confirm-${confirmAction?.type}-btn`}
            >
              {confirmAction?.type === 'delete' ? <Trash2 className="w-4 h-4 me-1" /> : <Archive className="w-4 h-4 me-1" />}
              {confirmAction?.type === 'delete' ? 'Delete' : 'Archive'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function WOCard({ wo, isRequester, isManager, processing, moveOptions, onMove, onMobileMenu, onDragStart, onNavigate, onArchive, onDelete }) {
  const { t } = useTranslation();
  const priorityColor = PRIORITY_COLORS[wo.priority] || PRIORITY_COLORS.MEDIUM;
  const isProcessing = processing === wo.id;

  return (
    <Card
      className={`bg-card border-border shadow-sm hover:shadow-md transition-shadow cursor-grab active:cursor-grabbing ${isProcessing ? 'opacity-50' : ''}`}
      draggable={!isRequester}
      onDragStart={onDragStart}
      data-testid={`kanban-card-${wo.id}`}
    >
      <CardContent className="p-3 space-y-2">
        {/* Priority strip + ID */}
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${priorityColor} flex-shrink-0`} />
          <span className="text-[11px] font-mono text-muted-foreground">{wo.id.slice(0, 8)}</span>
          <Badge variant="outline" className="text-[10px] ms-auto px-1.5 py-0">{wo.status}</Badge>
        </div>

        {/* Title */}
        <p className="text-sm font-medium text-foreground leading-tight line-clamp-2 cursor-pointer hover:text-brand" onClick={onNavigate} data-testid={`card-title-${wo.id}`}>
          {wo.title || t('kanban.untitled')}
        </p>

        {/* Branch + Room/Asset */}
        <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
          <Building2 className="w-3 h-3 flex-shrink-0" />
          <span className="truncate">{wo.branch_name}</span>
          <span className="mx-0.5">/</span>
          <span className="truncate">{wo.room_name || t('common.noRoom')}</span>
        </div>

        {/* Where the maintenance came from */}
        <div>
          <SourceBadge source={wo.maintenance_source} compact />
        </div>

        {/* People */}
        <div className="flex items-center justify-between text-[11px]">
          <div className="flex items-center gap-1 text-muted-foreground truncate">
            <User className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">{wo.requester_name || '—'}</span>
          </div>
          {wo.assigned_to_name && (
            <div className="flex items-center gap-1 text-brand truncate">
              <Wrench className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{wo.assigned_to_name}</span>
            </div>
          )}
        </div>

        {/* Mobile action button */}
        {!isRequester && moveOptions.length > 0 && (
          <div className="flex gap-1 pt-1 sm:hidden">
            {moveOptions.length === 1 ? (
              <Button size="sm" variant="outline" className="w-full h-7 text-xs" onClick={() => onMove(moveOptions[0].target)} disabled={isProcessing}>
                {moveOptions[0].label}
              </Button>
            ) : (
              <Button size="sm" variant="outline" className="w-full h-7 text-xs" onClick={onMobileMenu} disabled={isProcessing}>
                <MoreHorizontal className="w-3 h-3 me-1" /> {t('kanban.moveTo')}
              </Button>
            )}
          </div>
        )}

        {/* Desktop hover actions */}
        {!isRequester && moveOptions.length > 0 && (
          <div className="hidden sm:flex gap-1 pt-1">
            {moveOptions.map((opt, i) => (
              <Button key={i} size="sm" variant={opt.target === 'REJECT' ? 'destructive' : 'outline'} className="flex-1 h-7 text-[11px]" onClick={() => onMove(opt.target)} disabled={isProcessing}>
                {opt.label}
              </Button>
            ))}
          </div>
        )}

        {/* Archive / Delete (manager only) */}
        {isManager && (
          <div className="flex gap-1 pt-1 border-t border-border/50 mt-1">
            <Button size="sm" variant="ghost" className="flex-1 h-7 text-[11px] text-muted-foreground hover:text-foreground" onClick={onArchive} disabled={isProcessing} data-testid={`archive-${wo.id}`}>
              <Archive className="w-3 h-3 me-1" /> Archive
            </Button>
            <Button size="sm" variant="ghost" className="flex-1 h-7 text-[11px] text-muted-foreground hover:text-red-500" onClick={onDelete} disabled={isProcessing} data-testid={`delete-${wo.id}`}>
              <Trash2 className="w-3 h-3 me-1" /> Delete
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
